import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import HomePage from '~/@pages_dashboard/home/HomePage'
import OrderPage from '~/@pages_investing/order/OrderPage'
import { FeeImmunityPage } from '~/@pages_investing/fee-immunity/FeeImmunityPage'
import AccountPage from '~/@pages_account-portfolio/PortfolioPage'
import WithdrawFundsPage from '~/@pages_account-funds/withdraw-funds/WithdrawFundsPage'
import AddFundsPage from '~/@pages_account-funds/add-funds/AddFundsPage'
import SettingsPage from '~/@pages_account-settings/SettingsPage'
import AdminOtpLoginPage from '~/@pages_admin/admin-otp-login/AdminOtpLoginPage'
import LoginPage from '~/@pages_auth/login/LoginPage'
import ForgotPasswordPage from '~/@pages_auth/forgot-password/ForgotPasswordPage'
import ResetPasswordPage from '~/@pages_auth/reset-password/ResetPasswordPage'
import RegisterPage from '~/@pages_auth/register/RegisterPage'
import { AuthenticatedRoute } from './helper-routes/AuthenticatedRoute'
import { NotAuthenticatedRoute } from './helper-routes/NotAuthenticatedRoute'
import { UnknownPageHandler } from './helper-routes/UnknownPageHandler'
import PhoneVerificationPage from '~/@pages_onboarding/phone-verification/PhoneVerificationPage'
import IdentityVerificationPage from '~/@pages_onboarding/identity-verification/IdentityVerificationPage'
import VeriffRedirectPage from '~/@pages_onboarding/identity-verification/VeriffRedirectPage'
import IdentityConfirmationPage from '~/@pages_onboarding/identity-confirmation/IdentityConfirmationPage'
import CompanyConfirmationPage from '~/@pages_onboarding/company-confirmation/CompanyConfirmationPage'
import CompanyConfirmationAdminPage from '~/@pages_onboarding/company-confirmation-admin/CompanyConfirmationAdminPage'
import InvestorAnalysisPage from '~/@pages_onboarding/investor-analysis/InvestorAnalysisPage'
import InvestorSignaturePage from '~/@pages_onboarding/investor-signature/InvestorSignaturePage'
import PendingApprovalPage from '~/@pages_onboarding/pending-approval/PendingApprovalPage'
import ProfilePage from '~/@pages_account-settings/profile/ProfilePage'
import BankAccountsPage from '~/@pages_account-settings/bank-accounts/BankAccountsPage'
import AffiliatePage from '~/@pages_account-settings/affiliate/AffiliatePage'
import AmbassadorPage from '~/@pages_account-settings/ambassador/AmbassadorPage'
import AmbassadorDetailPage from '~/@pages_account-settings/ambassador-detail/AmbassadorDetailPage'
import MyAmbassadorPage from '~/@pages_account-settings/my-ambassador/MyAmbassadorPage'
import SecurityPage from '~/@pages_account-settings/security/SecurityPage'
import MarketingNotificationsPage from '~/@pages_account-settings/notifications/MarketingNotificationsPage'
import ProfilePasswordPage from '~/@pages_account-settings/password/ProfilePasswordPage'
import ProjectsPage from '~/@pages_investing/projects/ProjectsPage'
import ProjectDetailPage from '~/@pages_investing/project-detail/ProjectDetailPage'
import AmbassadorLandingPage from '~/@pages_ambassador-landing/AmbassadorLandingPage'
import { FinancialOverviewTab } from '~/@pages_account-portfolio/financial-overview/FinancialOverviewTab'
import { InvestmentsTab } from '~/@pages_account-portfolio/investments/InvestmentsTab'
import { InvestorTransactionsTab } from '~/@pages_account-portfolio/transactions/TransactionsTab'
import { DocumentsTab } from '~/@pages_account-portfolio/documents/DocumentsTab'
import AmbassadorRegistrationPage from '~/@pages_ambassador-registration/AmbassadorRegistrationPage'
import UserOtpLoginPage from '~/@pages_auth/user-otp-login/UserOtpLoginPage'

type Props = {
  isAuthenticated: boolean
}

export function AppRouter({ isAuthenticated }: Props) {
  const location = useLocation()

  return (
    <>
      <Routes location={location}>
        <Route
          element={
            <AuthenticatedRoute
              isAuthenticated={isAuthenticated}
              withTopNavigation
            />
          }>
          <Route path="/" element={<HomePage />} />
          <Route element={<SettingsPage />}>
            <Route
              path="/settings/*"
              element={
                <Routes>
                  <Route
                    path="/"
                    element={<Navigate to="/settings/profile" />}
                  />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/password" element={<ProfilePasswordPage />} />
                  <Route path="/bank-accounts" element={<BankAccountsPage />} />
                  <Route path="/affiliate" element={<AffiliatePage />} />
                  <Route path="/ambassador" element={<AmbassadorPage />} />
                  <Route
                    path="/ambassador/:ambassadorId"
                    element={<AmbassadorDetailPage />}
                  />
                  <Route path="/my-ambassador" element={<MyAmbassadorPage />} />
                  <Route path="/security" element={<SecurityPage />} />
                  <Route
                    path="/notifications"
                    element={<MarketingNotificationsPage />}
                  />
                </Routes>
              }
            />
          </Route>
          <Route
            path="/projects/*"
            element={
              <Routes>
                <Route path="/" element={<ProjectsPage />} />
                <Route
                  path="/:businessId/:stageNumber"
                  element={<ProjectDetailPage />}
                />
              </Routes>
            }
          />
          <Route
            path="/order/:businessId/:stageNumber"
            element={<OrderPage />}
          />
          <Route
            path="/account/withdraw-funds"
            element={<WithdrawFundsPage />}
          />
          <Route path="/account/add-funds" element={<AddFundsPage />} />
          <Route element={<AccountPage />}>
            <Route
              path="/account/*"
              element={
                <Routes>
                  <Route path="/" element={<FinancialOverviewTab />} />
                  <Route path="/investments" element={<InvestmentsTab />} />
                  <Route
                    path="/transactions"
                    element={<InvestorTransactionsTab />}
                  />
                  <Route path="/documents" element={<DocumentsTab />} />
                </Routes>
              }
            />
          </Route>
          <Route path="/fee-immunity" element={<FeeImmunityPage />} />
        </Route>

        <Route
          element={<AuthenticatedRoute isAuthenticated={isAuthenticated} />}>
          <Route
            path="/get-started/*"
            element={
              <Routes>
                <Route
                  path="/phone-verification"
                  element={<PhoneVerificationPage />}
                />
                <Route
                  path="/identity-verification"
                  element={<IdentityVerificationPage />}
                />
                <Route
                  path="/identity-verification/veriff-callback"
                  element={<VeriffRedirectPage />}
                />
                <Route
                  path="/identity-confirmation"
                  element={<IdentityConfirmationPage />}
                />
                <Route
                  path="/company-confirmation"
                  element={<CompanyConfirmationPage />}
                />
                <Route
                  path="/company-confirmation-admin"
                  element={<CompanyConfirmationAdminPage />}
                />
                <Route
                  path="/investor-analysis"
                  element={<InvestorAnalysisPage />}
                />
                <Route
                  path="/investor-signature"
                  element={<InvestorSignaturePage />}
                />
                <Route
                  path="/pending-approval"
                  element={<PendingApprovalPage />}
                />
              </Routes>
            }
          />
        </Route>

        <Route
          element={<NotAuthenticatedRoute isAuthenticated={isAuthenticated} />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/reset-password/:token"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/ambassador-registration"
            element={<AmbassadorRegistrationPage />}
          />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/admin-login/:otp" element={<AdminOtpLoginPage />} />
          <Route path="/otp-login" element={<UserOtpLoginPage />} />
          <Route
            path="/affiliate/:tokenAffilRecommender"
            element={<RegisterPage />}
          />
          <Route
            path="/ambassador-registration"
            element={<AmbassadorRegistrationPage />}
          />
          {/* Nateraz zakomentovane, asi na to nadviazeme cez novu funkcnost pozvi priatela, alebo nieco podobne. */}
          {/* <Route path="/broker/:brokerCode" element={<BrokerPage />} /> */}
        </Route>

        <Route
          path="/ambassador/:ambassadorCode"
          element={<AmbassadorLandingPage isAuthenticated={isAuthenticated} />}
        />

        <Route
          path="*"
          element={<UnknownPageHandler isAuthenticated={isAuthenticated} />}
        />
      </Routes>
    </>
  )
}
